import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { getImageUrl } from '../config/apiConfig';
import { useNavigate, useParams } from 'react-router-dom';
import dataService from '../services/dataService';
import {
    UserIcon,
    MapPinIcon,
    EyeIcon,
    CalendarIcon,
    ArrowPathIcon,
    EnvelopeIcon
} from '@heroicons/react/24/outline';

import { useAuth } from '../context/AuthContext';

const RenewMembers = () => {
    const { user: currentUser } = useAuth();
    const chapterLabel = currentUser?.namingConvention?.chapterLabel || 'Chapter';
    const navigate = useNavigate();
    const { orgCode } = useParams();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [selectedMembers, setSelectedMembers] = useState([]);

    // Renew Modal State
    const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
    const [memberToRenew, setMemberToRenew] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [renewalDate, setRenewalDate] = useState('');
    const [immediateRenew, setImmediateRenew] = useState(true);

    const calculateOneYearLater = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        d.setFullYear(d.getFullYear() + 1);
        return d.toISOString().split('T')[0];
    };

    const handleOpenRenewModal = (member) => {
        setMemberToRenew(member);
        const todayStr = new Date().toISOString().split('T')[0];
        setStartDate(todayStr);
        setImmediateRenew(true);
        const defaultEnd = calculateOneYearLater(todayStr);
        setEndDate(defaultEnd);
        setRenewalDate(defaultEnd);
        setIsRenewModalOpen(true);
    };

    const handleStartDateChange = (val) => {
        setStartDate(val);
        const todayStr = new Date().toISOString().split('T')[0];
        setImmediateRenew(val === todayStr);
        const calculatedEnd = calculateOneYearLater(val);
        setEndDate(calculatedEnd);
        setRenewalDate(calculatedEnd);
    };

    const handleImmediateToggle = (checked) => {
        setImmediateRenew(checked);
        if (checked) {
            const todayStr = new Date().toISOString().split('T')[0];
            setStartDate(todayStr);
            const calculatedEnd = calculateOneYearLater(todayStr);
            setEndDate(calculatedEnd);
            setRenewalDate(calculatedEnd);
        }
    };

    const handleConfirmRenewal = async (e) => {
        e.preventDefault();
        if (!startDate || !endDate || !renewalDate) {
            alert('Please select all date fields.');
            return;
        }

        setLoading(true);
        try {
            const res = await dataService.renewMember(memberToRenew.id, {
                membership_start_date: startDate,
                membership_end_date: endDate,
                membership_renewal_date: renewalDate
            });
            if (res.data.success) {
                alert('Membership renewed successfully!');
                setIsRenewModalOpen(false);
                setMemberToRenew(null);
                fetchRenewMembers();
            } else {
                alert('Failed to renew membership.');
            }
        } catch (error) {
            console.error("Renewal failed:", error);
            alert(error.response?.data?.message || 'Failed to renew membership.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRenewMembers();
    }, []);

    const fetchRenewMembers = async () => {
        setLoading(true);
        try {
            const res = await dataService.getRenewMembers();
            if (res.data.success) {
                setMembers(res.data.data);
                setCurrentYear(res.data.year);
            }
        } catch (error) {
            console.error("Failed to fetch renew members", error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedMembers(members.map(m => m.id));
        } else {
            setSelectedMembers([]);
        }
    };

    const handleSelectMember = (id) => {
        setSelectedMembers(prev => 
            prev.includes(id) ? prev.filter(mId => mId !== id) : [...prev, id]
        );
    };

    const handleSendEmail = async () => {
        setLoading(true);
        try {
            const res = await dataService.sendRenewalReminders(selectedMembers);
            if (res.data.success) {
                alert('Renewal reminders have been sent successfully to the selected members.');
                setSelectedMembers([]);
            } else {
                alert('Something went wrong. Please try again.');
            }
        } catch (error) {
            console.error("Failed to send renewal reminders", error);
            alert(error.response?.data?.message || 'Failed to send reminders.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-8 max-w-7xl mx-auto px-4 mt-6 pb-10">
            {/* Header */}
            <div className="premium-card p-8 bg-white">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2.5 bg-primary-50 rounded-xl">
                                <ArrowPathIcon className="w-6 h-6 text-primary-600" />
                            </div>
                            <h1 className="text-3xl font-black text-slate-900 tracking-tight">
                                Membership Renewals {currentYear}
                            </h1>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                            <p className="text-[13px] font-semibold text-slate-500 uppercase tracking-widest">
                                {members.length} Members Due for Renewal
                            </p>
                        </div>
                    </div>
                    {selectedMembers.length > 0 && (
                        <button
                            onClick={handleSendEmail}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold transition-all shadow-md hover:shadow-lg active:scale-95"
                        >
                            <EnvelopeIcon className="w-5 h-5" />
                            Send Email ({selectedMembers.length})
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-32 premium-card">
                    <div className="relative">
                        <div className="w-12 h-12 rounded-full border-4 border-slate-100 border-t-primary-600 animate-spin" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-2 h-2 rounded-full bg-primary-600 animate-ping" />
                        </div>
                    </div>
                    <p className="mt-4 text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] animate-pulse">
                        Loading Renewals
                    </p>
                </div>
            ) : (
                <div className="premium-card p-6">
                    {members.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-24 text-slate-300">
                            <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 mb-4">
                                <ArrowPathIcon className="w-12 h-12 opacity-20" />
                            </div>
                            <p className="text-sm font-bold uppercase tracking-widest opacity-40">
                                No renewals scheduled for {currentYear}
                            </p>
                        </div>
                    ) : (
                        <div>
                            {/* Select All Bar */}
                            <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-100 rounded-2xl mb-6">
                                <input 
                                    type="checkbox" 
                                    className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                                    checked={selectedMembers.length === members.length && members.length > 0}
                                    onChange={handleSelectAll}
                                />
                                <span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">
                                    Select All Members ({members.length})
                                </span>
                            </div>

                            {/* Card Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {members.map((member) => (
                                    <div
                                        key={member.id}
                                        className="group relative bg-white border-2 border-slate-100 rounded-[2rem] p-6 hover:border-primary-200 hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
                                    >
                                        <div>
                                            {/* Header */}
                                            <div className="flex items-start gap-4 mb-6">
                                                {/* Checkbox and Profile Image */}
                                                <div className="flex items-center gap-3 shrink-0">
                                                    <input 
                                                        type="checkbox" 
                                                        className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500 cursor-pointer shrink-0"
                                                        checked={selectedMembers.includes(member.id)}
                                                        onChange={() => handleSelectMember(member.id)}
                                                    />
                                                    <div className="w-14 h-14 rounded-2xl bg-white overflow-hidden border-2 border-slate-100 flex-shrink-0 shadow-sm">
                                                        {member.profile_image ? (
                                                            <img
                                                                src={getImageUrl(member.profile_image)}
                                                                alt={member.name}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-slate-300 bg-gradient-to-br from-slate-50 to-slate-100">
                                                                <UserIcon className="w-6 h-6" />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <p className="text-base font-black text-slate-900 truncate leading-tight group-hover:text-primary-600 transition-colors">
                                                        {member.name}
                                                    </p>
                                                    {member.category_name && (
                                                        <span className="inline-flex mt-1 px-2.5 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider bg-primary-50 text-primary-600 border border-primary-100">
                                                            {member.category_name}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Details Grid */}
                                            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-100 mb-6">
                                                <div className="col-span-2">
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                        {chapterLabel}
                                                    </p>
                                                    <div className="flex items-center gap-1.5 text-slate-700">
                                                        <MapPinIcon className="w-4 h-4 text-slate-400" />
                                                        <span className="text-xs font-bold">
                                                            {member.chapter || 'Global'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                        End Date
                                                    </p>
                                                    <div className="flex items-center gap-1.5 text-slate-600">
                                                        <CalendarIcon className="w-4 h-4 text-slate-400" />
                                                        <span className="text-xs font-semibold">
                                                            {formatDate(member.membership_end_date)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                        Renewal Date
                                                    </p>
                                                    <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-[10px] font-bold">
                                                        <ArrowPathIcon className="w-3 h-3 text-amber-600 shrink-0" />
                                                        <span>
                                                            {formatDate(member.membership_renewal_date)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Actions Footer */}
                                        <div className="flex items-center gap-3 pt-4 border-t border-slate-50">
                                            <button
                                                onClick={() => navigate(`/${orgCode}/admin/members/${member.id}`)}
                                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-50 hover:bg-slate-900 hover:text-white hover:border-slate-900 text-slate-600 rounded-xl font-bold text-xs border border-slate-100 transition-all active:scale-95 shadow-sm"
                                            >
                                                <EyeIcon className="w-4 h-4" />
                                                View Profile
                                            </button>
                                            <button
                                                onClick={() => handleOpenRenewModal(member)}
                                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl font-bold text-xs transition-all active:scale-95 border border-emerald-100 shadow-sm"
                                            >
                                                <ArrowPathIcon className="w-4 h-4" />
                                                Renew Member
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Renew Member Modal */}
            <AnimatePresence>
                {isRenewModalOpen && memberToRenew && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col">
                            {/* Header */}
                            <div className="relative bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-6 text-white">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsRenewModalOpen(false);
                                        setMemberToRenew(null);
                                    }}
                                    className="absolute top-4 right-4 p-2 text-white/85 hover:text-white hover:bg-white/15 rounded-xl transition-all"
                                >
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                                <h3 className="text-xl font-black tracking-tight">Renew Membership</h3>
                                <p className="text-emerald-100 text-xs mt-1 font-semibold uppercase tracking-wider">
                                    Member: {memberToRenew.name}
                                </p>
                            </div>

                            {/* Body */}
                            <form onSubmit={handleConfirmRenewal} className="p-8 space-y-6">
                                <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 flex items-center gap-3">
                                    <ArrowPathIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                                    <div className="text-xs text-emerald-800 font-medium">
                                        Renewing this membership will update the membership start date, end date, and renewal date.
                                    </div>
                                </div>

                                {/* Current Dates View */}
                                <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-4 grid grid-cols-3 gap-4 text-center shadow-inner">
                                    <div>
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Current Start</p>
                                        <p className="text-xs font-extrabold text-slate-700 mt-1">{formatDate(memberToRenew.membership_start_date)}</p>
                                    </div>
                                    <div className="border-x border-slate-200/80">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Current End</p>
                                        <p className="text-xs font-extrabold text-slate-700 mt-1">{formatDate(memberToRenew.membership_end_date)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Current Renewal</p>
                                        <p className="text-xs font-extrabold text-slate-700 mt-1">{formatDate(memberToRenew.membership_renewal_date)}</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {/* Immediate Renewal Checkbox */}
                                    <label className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100/50 transition-all select-none">
                                        <input
                                            type="checkbox"
                                            checked={immediateRenew}
                                            onChange={(e) => handleImmediateToggle(e.target.checked)}
                                            className="w-5 h-5 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer"
                                        />
                                        <div className="flex-1">
                                            <p className="text-sm font-bold text-slate-800">Renew Immediately</p>
                                            <p className="text-[11px] text-slate-500 font-semibold">Start the new membership period from today</p>
                                        </div>
                                    </label>

                                    {/* Start Date */}
                                    <div>
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
                                            Membership Start Date
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="date"
                                                value={startDate}
                                                onChange={(e) => handleStartDateChange(e.target.value)}
                                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* End Date */}
                                    <div>
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
                                            Membership End Date
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="date"
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Renewal Date */}
                                    <div>
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
                                            Membership Renewal Date
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="date"
                                                value={renewalDate}
                                                onChange={(e) => setRenewalDate(e.target.value)}
                                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Footer Actions */}
                                <div className="flex gap-3 pt-4 border-t border-slate-100">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsRenewModalOpen(false);
                                            setMemberToRenew(null);
                                        }}
                                        className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold transition-all text-sm active:scale-95"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold transition-all text-sm shadow-lg shadow-emerald-100 active:scale-95 disabled:opacity-50"
                                    >
                                        {loading ? 'Processing...' : 'Confirm Renewal'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default RenewMembers;
